import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Stack,
  Divider,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import {
  Palette as PaletteIcon,
  Language as LanguageIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  SettingsBrightness as SettingsBrightnessIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { useThemeContext, ThemeMode } from '../contexts/ThemeContext';


interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({ open, onClose, ...props }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { language, changeLanguage } = useLanguage();
  const { mode, setThemeMode, effectiveMode } = useThemeContext();
  // Remove unused variables

  // Handle theme change
  const handleThemeChange = (event: SelectChangeEvent<string>) => {
    setThemeMode(event.target.value as ThemeMode);
  };

  // Handle language change
  const handleLanguageChange = (event: SelectChangeEvent<string>) => {
    const newLanguage = event.target.value;
    if (newLanguage !== language) {
      changeLanguage(newLanguage);
    }
  };

  // Remove unused function

  // Track if a select dropdown is open
  const [isSelectOpen, setIsSelectOpen] = React.useState(false);

  // Handle select open state
  const handleSelectOpen = () => {
    setIsSelectOpen(true);
  };

  // Handle select close state
  const handleSelectClose = () => {
    setIsSelectOpen(false);
  };

  // Create a ref to track if a select is open
  const selectOpenRef = React.useRef(isSelectOpen);

  // Update the ref when the state changes
  React.useEffect(() => {
    selectOpenRef.current = isSelectOpen;
  }, [isSelectOpen]);

  // Prevent modal from closing when clicking on the content
  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // Remove unused function

  // Custom onClose handler for the Dialog
  const handleDialogClose = (event: React.SyntheticEvent<any>, reason: string) => {
    // Don't close the dialog if a select dropdown is open
    if (isSelectOpen && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      onClose={handleDialogClose}
      // This prevents the dialog from closing when clicking outside
      // but only when a select dropdown is open
      data-disable-backdrop-click={isSelectOpen ? 'true' : 'false'}
      data-testid="settings-modal"
      // Add a click handler to the backdrop to prevent closing when select is open
      slotProps={{
        backdrop: {
          onClick: (e: React.MouseEvent) => {
            // If a select is open, prevent the dialog from closing
            if (isSelectOpen) {
              e.preventDefault();
              e.stopPropagation();
            }
          }
        }
      }}
      // Apply custom styles to the Paper component
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: theme.palette.mode === 'dark'
            ? '0 8px 32px rgba(0, 0, 0, 0.5)'
            : '0 8px 32px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden'
        },
        onClick: (e: React.MouseEvent) => {
          e.stopPropagation();
          e.preventDefault();
        } // Prevent clicks on the Paper from bubbling up
      }}
      {...props}
    >
      <DialogTitle
        onMouseDown={handleMouseDown}
        onClick={(e) => e.stopPropagation()}
      >
        {t('settings.title')}
      </DialogTitle>
      <DialogContent
        onMouseDown={handleMouseDown}
        onClick={(e) => e.stopPropagation()}
      >
        <Typography variant="body1" color="textSecondary" paragraph>
          {t('settings.account')}
        </Typography>

        {/* Theme Settings */}
        <Paper
          elevation={0}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          sx={{
            p: 3,
            mb: 3,
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center" mb={2}>
            <PaletteIcon color="primary" />
            <Typography variant="h6">{t('settings.appearance')}</Typography>
          </Stack>
          <Divider sx={{ mb: 2 }} />

          <Stack spacing={3}>
            <Box>
              <FormControl
                fullWidth
                variant="outlined"
                sx={{ mt: 1 }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <InputLabel id="theme-select-label">{t('settings.theme')}</InputLabel>
                <Select
                  labelId="theme-select-label"
                  id="theme-select"
                  value={mode}
                  onChange={handleThemeChange}
                  label={t('settings.theme')}
                  onOpen={handleSelectOpen}
                  onClose={handleSelectClose}
                  data-testid="select-dropdown"
                  // Prevent event propagation
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                  // Simple MenuProps configuration
                  MenuProps={{
                    // Prevent the menu from closing when clicking inside
                    disableAutoFocusItem: true,
                    disableScrollLock: true,
                    // Add data attribute to the Paper component
                    PaperProps: {
                      'data-disable-backdrop-click': 'true',
                      'data-testid': 'select-dropdown',
                      onClick: (e: React.MouseEvent) => {
                        e.stopPropagation();
                        e.preventDefault();
                      },
                      onMouseDown: (e: React.MouseEvent) => {
                        e.stopPropagation();
                        e.preventDefault();
                      }
                    },
                    // Prevent clicks on the menu from closing the dialog
                    onClick: (e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }
                  }}
                  sx={{
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }
                  }}
                >
                  <MenuItem
                    value="system"
                    data-testid="select-dropdown"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <SettingsBrightnessIcon fontSize="small" color="primary" />
                      <Typography>{t('settings.systemDefault')}</Typography>
                      {mode === 'system' && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          ({effectiveMode === 'dark' ? t('settings.darkMode') : t('settings.lightMode')})
                        </Typography>
                      )}
                    </Stack>
                  </MenuItem>
                  <MenuItem
                    value="light"
                    data-testid="select-dropdown"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LightModeIcon fontSize="small" color="primary" />
                      <Typography>{t('settings.lightMode')}</Typography>
                    </Stack>
                  </MenuItem>
                  <MenuItem
                    value="dark"
                    data-testid="select-dropdown"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <DarkModeIcon fontSize="small" color="primary" />
                      <Typography>{t('settings.darkMode')}</Typography>
                    </Stack>
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Stack>
        </Paper>

        {/* Language Settings */}
        <Paper
          elevation={0}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          sx={{
            p: 3,
            mb: 3,
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center" mb={2}>
            <LanguageIcon color="primary" />
            <Typography variant="h6">{t('settings.language')}</Typography>
          </Stack>
          <Divider sx={{ mb: 2 }} />

          <Stack spacing={3}>
            <Box>
              <FormControl
                fullWidth
                variant="outlined"
                sx={{ mt: 1 }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <InputLabel id="language-select-label">{t('common.language')}</InputLabel>
                <Select
                  labelId="language-select-label"
                  id="language-select"
                  value={language}
                  onChange={handleLanguageChange}
                  label={t('common.language')}
                  onOpen={handleSelectOpen}
                  onClose={handleSelectClose}
                  data-testid="select-dropdown"
                  // Prevent event propagation
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                  // Simple MenuProps configuration
                  MenuProps={{
                    // Prevent the menu from closing when clicking inside
                    disableAutoFocusItem: true,
                    disableScrollLock: true,
                    // Add data attribute to the Paper component
                    PaperProps: {
                      'data-disable-backdrop-click': 'true',
                      'data-testid': 'select-dropdown',
                      onClick: (e: React.MouseEvent) => {
                        e.stopPropagation();
                        e.preventDefault();
                      },
                      onMouseDown: (e: React.MouseEvent) => {
                        e.stopPropagation();
                        e.preventDefault();
                      }
                    },
                    // Prevent clicks on the menu from closing the dialog
                    onClick: (e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }
                  }}
                  sx={{
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }
                  }}
                >
                  <MenuItem
                    value="en"
                    data-testid="select-dropdown"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        component="img"
                        src="/assets/flags/gb.svg"
                        alt="English"
                        sx={{ width: 20, height: 20, borderRadius: '50%' }}
                      />
                      <Typography>English</Typography>
                    </Stack>
                  </MenuItem>
                  <MenuItem
                    value="sv"
                    data-testid="select-dropdown"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        component="img"
                        src="/assets/flags/se.svg"
                        alt="Svenska"
                        sx={{ width: 20, height: 20, borderRadius: '50%' }}
                      />
                      <Typography>Svenska</Typography>
                    </Stack>
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Stack>
        </Paper>
      </DialogContent>
      <DialogActions
        onMouseDown={handleMouseDown}
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          variant="contained"
          color="primary"
        >
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SettingsModal;
